import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import BookReader from '../components/BookReader';
import { BookCoverPlaceholder } from '../components/BookCard';
import BookmarkButton from '../components/BookmarkButton';
import ReadingStatusBadge from '../components/ReadingStatusBadge';
import StarRating from '../components/StarRating';
import { useLibrary } from '../context/LibraryContext';
import { apiUrl } from '../api';

// Format label -> icon mapping
const FORMAT_ICONS = {
  EPUB: '📱',
  MOBI: '📲',
  PDF: '📄',
  'PLAIN TEXT': '📝',
  HTML: '🌐',
};

function SkeletonDetail() {
  return (
    <div className="page-container py-10 animate-pulse">
      {/* Back button skeleton */}
      <div className="h-8 skeleton rounded w-24 mb-8" />

      <div className="grid lg:grid-cols-3 gap-10">
        {/* Cover skeleton */}
        <div className="lg:col-span-1">
          <div className="skeleton-shimmer rounded-xl" style={{ paddingTop: '140%', position: 'relative' }}>
            <div className="absolute inset-0 bg-primary/50 rounded-xl" />
          </div>
        </div>

        {/* Info skeleton */}
        <div className="lg:col-span-2 space-y-4">
          <div className="h-8 skeleton rounded w-3/4" />
          <div className="h-5 skeleton rounded w-1/2" />
          <div className="h-4 skeleton rounded w-1/4" />
          <div className="flex gap-2 mt-4">
            <div className="h-10 skeleton rounded-lg w-32" />
            <div className="h-10 skeleton rounded-lg w-32" />
          </div>
          <div className="mt-6 space-y-2">
            <div className="h-4 skeleton rounded w-full" />
            <div className="h-4 skeleton rounded w-5/6" />
            <div className="h-4 skeleton rounded w-4/6" />
          </div>
        </div>
      </div>
    </div>
  );
}

function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addRecentlyViewed } = useLibrary();

  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReader, setShowReader] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [downloading, setDownloading] = useState(null); // format label being downloaded

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setShowReader(false);
    setImgError(false);
    setImgLoaded(false);

    const fetchBook = async () => {
      try {
        const res = await fetch(apiUrl(`/api/books/${id}`));
        if (!res.ok) {
          const status = res.status;
          if (!cancelled) setError(status === 404 ? 'Book not found.' : status === 400 ? 'Invalid book ID.' : 'Failed to load book.');
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setBook(data);
          addRecentlyViewed(data);
        }
      } catch {
        if (!cancelled) setError('Failed to load book. Please check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBook();
    return () => { cancelled = true; };
  }, [id, addRecentlyViewed]);

  // Must be defined before any early returns — hooks must always run in the same order
  const isDownloadableUrl = (url) => {
    try {
      return /\.(epub|pdf|mobi|txt|html|htm|zip)$/i.test(new URL(url).pathname);
    } catch { return false; }
  };

  const handleDownload = useCallback(async (url, fmt) => {
    if (!isDownloadableUrl(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setDownloading(fmt);
    try {
      const ext = url.split('/').pop()?.split('.').pop() || fmt.toLowerCase();
      const bookTitle = book?.title || 'book';
      const filename = `${bookTitle}.${ext}`;
      const proxyUrl = apiUrl(`/api/books/proxy-download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`);
      const res = await fetch(proxyUrl);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch {
      alert('Download failed. Please try again.');
    } finally {
      setDownloading(null);
    }
  }, [book]);

  if (loading) return <SkeletonDetail />;

  if (error) {
    return (
      <div className="page-container py-20 text-center">
        <span className="text-6xl block mb-6">😕</span>
        <h1 className="text-2xl font-bold text-white mb-3">Oops!</h1>
        <p className="text-gray-400 mb-6">{error}</p>
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => navigate(-1)} className="btn-secondary">
            Go Back
          </button>
          <Link to="/" className="btn-primary">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  if (!book) return null;

  const {
    title,
    authors,
    cover,
    subjects,
    bookshelves,
    languages,
    downloadCount,
    readUrl,
    downloads,
  } = book;


  const hasCover = cover && !imgError;
  const downloadEntries = Object.entries(downloads || {});
  const allSubjects = [...(subjects || []), ...(bookshelves || [])].slice(0, 15);

  const languageNames = (languages || []).map((code) => {
    try {
      return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) || code.toUpperCase();
    } catch {
      return code.toUpperCase();
    }
  });

  return (
    <div className="py-10 animate-fade-in">
      <div className="page-container">
        {/* Breadcrumb / back */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-accent transition-colors duration-200">Home</Link>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <button
            onClick={() => navigate(-1)}
            className="hover:text-accent transition-colors duration-200"
          >
            Back
          </button>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-gray-300 truncate max-w-xs">{title}</span>
        </nav>

        <div className="grid lg:grid-cols-3 gap-10 mb-12">
          {/* ── Cover Column ────────────────────────────────────────── */}
          <div className="lg:col-span-1">
            <div
              className="rounded-xl overflow-hidden shadow-2xl border border-border relative"
              style={{ paddingTop: '140%' }}
            >
              <div className="absolute inset-0">
                {hasCover ? (
                  <>
                    {!imgLoaded && (
                      <div className="absolute inset-0 skeleton-shimmer" />
                    )}
                    <img
                      src={cover}
                      alt={`Cover of ${title}`}
                      className={`w-full h-full object-cover transition-opacity duration-500 ${
                        imgLoaded ? 'opacity-100' : 'opacity-0'
                      }`}
                      onLoad={() => setImgLoaded(true)}
                      onError={() => setImgError(true)}
                    />
                  </>
                ) : (
                  <BookCoverPlaceholder title={title} />
                )}
              </div>
            </div>

            {/* Download count */}
            {downloadCount > 0 && (
              <div className="mt-4 flex items-center justify-end">
                <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span>{downloadCount.toLocaleString()} downloads</span>
                </div>
              </div>
            )}

            {/* Languages */}
            {languageNames.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Available In
                </h4>
                <div className="flex flex-wrap gap-2">
                  {languageNames.map((lang, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 rounded-full text-xs border border-border text-gray-400"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Info Column ─────────────────────────────────────────── */}
          <div className="lg:col-span-2">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
              {title}
            </h1>

            {authors && (
              <p className="text-gold text-lg font-medium mb-6">by {authors}</p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 mb-8">
              <button
                onClick={() => setShowReader((prev) => !prev)}
                className={`btn-primary gap-2 ${showReader ? 'bg-accent-light' : ''}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477
                       3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253
                       m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253
                       v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                {showReader ? 'Close Reader' : 'Read Online'}
              </button>

              {downloadEntries.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {downloadEntries.slice(0, 4).map(([fmt, url]) => (
                    <button
                      key={fmt}
                      onClick={() => handleDownload(url, fmt)}
                      disabled={downloading === fmt}
                      className="flex items-center gap-1.5 btn-secondary text-sm py-2 px-3 disabled:opacity-60"
                    >
                      {downloading === fmt
                        ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        : <span>{FORMAT_ICONS[fmt.toUpperCase()] || '⬇️'}</span>}
                      {downloading === fmt ? 'Downloading…' : fmt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Subjects/Genres */}
            {allSubjects.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Subjects &amp; Genres
                </h3>
                <div className="flex flex-wrap gap-2">
                  {allSubjects.map((subject, i) => (
                    <Link
                      key={i}
                      to={`/search?q=${encodeURIComponent(subject)}`}
                      className="px-3 py-1.5 rounded-full text-xs border border-border/70
                                 text-gray-400 hover:text-accent hover:border-accent/60
                                 hover:bg-accent/10 transition-all duration-200"
                    >
                      {subject}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* All downloads section */}
            {downloadEntries.length > 0 && (
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Formats
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {downloadEntries.map(([fmt, url]) => (
                    <button
                      key={fmt}
                      onClick={() => handleDownload(url, fmt)}
                      disabled={downloading === fmt}
                      className="flex items-center gap-2 p-3 rounded-lg bg-surface border border-border/50
                                 hover:border-accent/60 hover:bg-accent/10 text-sm text-gray-300
                                 hover:text-accent transition-all duration-200 group text-left
                                 disabled:opacity-60 disabled:cursor-wait w-full"
                    >
                      <span className="text-lg group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                        {downloading === fmt
                          ? <span className="inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          : (FORMAT_ICONS[fmt.toUpperCase()] || '📁')}
                      </span>
                      <div>
                        <div className="font-medium">{downloading === fmt ? 'Downloading…' : fmt}</div>
                        <div className="text-xs text-gray-600">Free download</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Library Actions ────────────────────────────────────── */}
            <div className="card p-5 mt-4">
              <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
                <svg className="w-4 h-4 text-accent" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
                My Library
              </h3>
              <div className="flex flex-wrap items-center gap-4">
                <BookmarkButton book={book} size="md" />
                <ReadingStatusBadge book={book} />
              </div>
              <div className="mt-4 pt-4 border-t border-border/50">
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-semibold">
                  Your Rating
                </p>
                <StarRating bookId={id} size="lg" />
              </div>
            </div>
          </div>
        </div>

        {/* ── Inline Reader ──────────────────────────────────────────── */}
        {showReader && (
          <div className="mt-4 mb-12 animate-slide-up">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-bold text-white">Reading: {title}</h2>
              <div
                className="h-px flex-1 bg-border"
                aria-hidden="true"
              />
            </div>
            <BookReader bookId={id} title={title} downloads={downloads} />
          </div>
        )}

        {/* ── Related searches ──────────────────────────────────────── */}
        {authors && (
          <div className="border-t border-border pt-8">
            <h3 className="text-sm text-gray-500 mb-3">
              More books like this
            </h3>
            <div className="flex flex-wrap gap-2">
              {authors.split(',').slice(0, 3).map((author) => {
                const a = author.trim();
                // Gutenberg author names are often "Last, First" format
                const displayName = a.includes(',')
                  ? a.split(',').reverse().join(' ').trim()
                  : a;
                return (
                  <Link
                    key={a}
                    to={`/search?q=${encodeURIComponent(displayName)}`}
                    className="btn-secondary text-sm"
                  >
                    More by {displayName}
                  </Link>
                );
              })}
              {subjects && subjects.slice(0, 2).map((sub) => (
                <Link
                  key={sub}
                  to={`/search?q=${encodeURIComponent(sub)}`}
                  className="btn-secondary text-sm"
                >
                  More: {sub.slice(0, 30)}{sub.length > 30 ? '...' : ''}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookDetail;
